// src/lib/dayCount.js

// ==========================================================
// DAY COUNT UTILITIES
// ==========================================================
import dayjs from "lib/dayjs";

/** ───────────────────────────────────────────────
 *  Core helpers
 *  ─────────────────────────────────────────────── */
function isLastDayOfFeb(d) {
  const mm = d.month() + 1;
  if (mm !== 2) return false;
  const day = d.date();
  const endFeb = d.endOf("month").date();
  return day === endFeb;
}

function spansLeapDay(d1, d2) {
  const start = dayjs(d1).startOf("day");
  const end = dayjs(d2).startOf("day");
  for (let y = start.year(); y <= end.year(); y++) {
    const feb29 = dayjs(`${y}-02-29`);
    if (feb29.isValid() && feb29.isAfter(start) && feb29.isBefore(end)) {
      return true;
    }
  }
  return false;
}

function daysBetween(a, b) {
  return dayjs(b).diff(dayjs(a), "day");
}

/** ───────────────────────────────────────────────
 *  Frequency helpers
 *  ─────────────────────────────────────────────── */
export function freqToPeriodsPerYear(freq = "semiannual") {
  const n = Number(freq);
  if (Number.isFinite(n) && n > 0) return n;

  const f = String(freq || "").toLowerCase().trim();
  if (f.startsWith("month")) return 12;
  if (f.startsWith("quarter")) return 4;
  if (f.startsWith("annual")) return 1;
  if (f.startsWith("semi")) return 2;
  return 2; // default: semiannual
}

/** ───────────────────────────────────────────────
 *  ICMA day-count helper
 *  ─────────────────────────────────────────────── */
export function dayCountICMA(prevCoupon, date, nextCoupon) {
  const num = daysBetween(prevCoupon, date);
  const den = Math.max(1, daysBetween(prevCoupon, nextCoupon));
  return num / den;
}

export function isCouponPeriodDayCount(method = "") {
  const dc = String(method || "").toLowerCase();
  return (
    dc === "act/act-icma" ||
    dc === "actual/actual-icma" ||
    dc === "icma" ||
    dc === "actact-icma" ||
    dc === "act/act-ust" ||
    dc === "actual/actual-ust" ||
    dc === "treasury" ||
    dc === "actact-ust"
  );
}

export function dayCountCouponPeriodFraction({
  start,
  end,
  nextCoupon,
  method = "30/360",
  frequency = "semiannual",
}) {
  const d1 = dayjs(start);
  const d2 = dayjs(end);
  const d3 = dayjs(nextCoupon);
  if (!d1.isValid() || !d2.isValid() || !d3.isValid() || d2.isBefore(d1)) {
    return 0;
  }

  if (!isCouponPeriodDayCount(method)) {
    return dayCountFraction(d1, d2, method);
  }

  const f = freqToPeriodsPerYear(frequency);
  const daysInPeriod = Math.max(1, daysBetween(d1, d3));
  const daysAccrued = Math.max(0, Math.min(daysBetween(d1, d2), daysInPeriod));
  return (daysAccrued / daysInPeriod) / f;
}

/** ───────────────────────────────────────────────
 *  Treasury (UST) day-count basis — ACT/ACT-UST
 *  per 31 CFR § 306.35
 *
 *  Correct handling for a long initial period (skipped first coupon):
 *  • If settling in the preceding semiannual window and accruing to the next coupon,
 *    DCF = (1/f)   // full semiannual
 *        + ( shortDays / daysInPrevSemiannual ) / f
 *    where shortDays = days from settlement up to the coupon boundary you skipped.
 *
 *  Example: Settle 2025-11-17, coupons 6/1 & 12/1; accrue to 6/1/2026:
 *    prevPrev = 2025-06-01, prev = 2025-12-01, next = 2026-06-01
 *    shortDays = 11/17→12/01 = 14
 *    prevSemiannualDays = 06/01→12/01 = 183
 *    DCF = 0.5 + (14/183)/2 = 0.538251...
 *  ─────────────────────────────────────────────── */
export function dayCountUST(start, end, frequency = "semiannual") {
  const f = freqToPeriodsPerYear(frequency);
  const months = 12 / f;

  const d1 = dayjs(start);
  const d2 = dayjs(end);
  if (!d2.isValid() || !d1.isValid() || d2.isBefore(d1)) return 0;

  // Build the local coupon frame anchored on the *accrual end* as the next coupon
  const nextCoupon = d2.clone();
  const prevCoupon = nextCoupon.clone().subtract(months, "month");
  const prevPrevCoupon = prevCoupon.clone().subtract(months, "month");

  const daysElapsed = d2.diff(d1, "day");
  const daysInPeriod = Math.max(1, nextCoupon.diff(prevCoupon, "day"));

  // CASE A: Exact full coupon period
  if (daysElapsed === daysInPeriod) return 1 / f;

  // CASE B: Long first period (skipping the first coupon)
  const isLongFirst =
    d1.isAfter(prevPrevCoupon) &&
    (d1.isBefore(prevCoupon) || d1.isSame(prevCoupon, "day")) &&
    d2.isSame(nextCoupon, "day") &&
    daysElapsed > daysInPeriod;

  if (isLongFirst) {
    const shortDays = Math.max(0, prevCoupon.diff(d1, "day"));
    const daysInPrevPeriod = Math.max(1, prevCoupon.diff(prevPrevCoupon, "day"));
    return 1 / f + (shortDays / daysInPrevPeriod) / f;
  }

  // CASE C: Normal short (or general in-period accrual)
  return (daysElapsed / daysInPeriod) / f;
}

/** ───────────────────────────────────────────────
 *  Generic day-count dispatcher
 *  ─────────────────────────────────────────────── */
export function dayCountFraction(start, end, method = "30/360") {
  const d1 = dayjs(start);
  const d2 = dayjs(end);
  if (!d1.isValid() || !d2.isValid() || d2.isBefore(d1)) return 0;

  switch ((method || "").toLowerCase().trim()) {
    case "30/360":
    case "30/360us":
    case "30/360uspsa":
    case "us muni: 30/360": {
      const Y1 = d1.year();
      const Y2 = d2.year();
      const M1 = d1.month() + 1;
      const M2 = d2.month() + 1;
      let D1 = d1.date();
      if (D1 === 31 || isLastDayOfFeb(d1)) D1 = 30;
      let D2 = d2.date();
      if ((D2 === 31 && D1 >= 30) || (isLastDayOfFeb(d2) && D1 >= 30)) D2 = 30;
      return (360 * (Y2 - Y1) + 30 * (M2 - M1) + (D2 - D1)) / 360;
    }

    case "30/360 non-eom": {
      const Y1 = d1.year();
      const Y2 = d2.year();
      const M1 = d1.month() + 1;
      const M2 = d2.month() + 1;
      const D1 = d1.date() === 31 ? 30 : d1.date();
      const D2 = d2.date() === 31 && D1 >= 30 ? 30 : d2.date();
      return (360 * (Y2 - Y1) + 30 * (M2 - M1) + (D2 - D1)) / 360;
    }

    case "30e/360":
    case "30/360e":
    case "eurobond": {
      const Y1 = d1.year();
      const Y2 = d2.year();
      const M1 = d1.month() + 1;
      const M2 = d2.month() + 1;
      const D1 = Math.min(30, d1.date());
      const D2 = Math.min(30, d2.date());
      return (360 * (Y2 - Y1) + 30 * (M2 - M1) + (D2 - D1)) / 360;
    }

    case "act/360":
    case "actual/360":
      return daysBetween(d1, d2) / 360;

    case "act/365":
    case "actual/365":
      return daysBetween(d1, d2) / 365;

    case "act/act-icma":
    case "actual/actual-icma":
    case "icma":
    case "actact-icma": {
      const frequency = "semiannual";
      const f = freqToPeriodsPerYear(frequency);
      const months = 12 / f;
      const maturityGuess = d2;
      const coupons = [];
      let c = maturityGuess.clone();

      for (let i = 0; i < 400; i++) {
        coupons.push(c);
        c = c.subtract(months, "month");
        if (c.isBefore(d1.subtract(2, "years"))) break;
      }

      coupons.sort((a, b) => a.valueOf() - b.valueOf());

      let prevCoupon = coupons[0];
      let nextCoupon = coupons[coupons.length - 1];
      for (const cd of coupons) {
        if (cd.isAfter(d1)) {
          nextCoupon = cd;
          break;
        }
        prevCoupon = cd;
      }

      const daysElapsed = d2.diff(d1, "day");
      const daysInPeriod = Math.max(1, nextCoupon.diff(prevCoupon, "day"));
      return (daysElapsed / daysInPeriod) / f;
    }

    case "act/act-ust":
    case "actual/actual-ust":
    case "treasury":
    case "actact-ust":
      return dayCountUST(d1, d2, "semiannual");

    // Default: ACT/ACT-ISDA (by-year slicing)
    case "act/act":
    case "actual/actual":
    default: {
      let s = d1.startOf("day");
      const end_ = d2.startOf("day");
      let frac = 0;

      while (s.isBefore(end_)) {
        const yearEnd = dayjs(`${s.year()}-12-31`).add(1, "day");
        const segEnd = end_.isBefore(yearEnd) ? end_ : yearEnd;
        const days = segEnd.diff(s, "day");
        const denom = s.isLeapYear() ? 366 : 365;
        frac += days / denom;
        s = segEnd;
      }

      return frac;
    }
  }
}

export { isLastDayOfFeb, spansLeapDay, daysBetween };
